import { UnifiedOnboardingData, UnifiedOnboardingStep } from './unifiedTypes';
import { validateEmail, validatePassword, validatePhoneNumber } from '../../utils/validation';

export function validateUnifiedStep(step: UnifiedOnboardingStep, data: UnifiedOnboardingData): boolean {
  switch (step) {
    case 0: // Account Creation
      return (
        validateEmail(data.email) &&
        validatePassword(data.password).isValid &&
        data.fullName.trim().length >= 2 &&
        validatePhoneNumber(data.phoneNumber) &&
        data.countryCode.length > 0
      );

    case 1: // Coach Personality
      return data.selectedPersonality !== null;

    case 2: // Payment (skip allowed for testing)
      return data.paymentCompleted; // Always valid since skip is allowed

    case 3: // Timing
      return (
        isValidTime(data.morningTime) &&
        isValidTime(data.eveningTime) &&
        data.timezone.length > 0 &&
        data.morningTime !== data.eveningTime
      );

    case 4: // Intensity
      return data.intensityLevel >= 1 && data.intensityLevel <= 5;

    case 5: // Additional Preferences
      return data.additionalPreferences.length <= 500; // Optional field, just validate length

    case 6: // Completion
      return true; // Always valid

    default:
      return false;
  }
}

function isValidTime(time: string): boolean {
  const timeRegex = /^([01]?[0-9]|2[0-3]):[0-5][0-9]$/;
  return timeRegex.test(time);
}

export function getUnifiedValidationErrors(step: UnifiedOnboardingStep, data: UnifiedOnboardingData): string[] {
  const errors: string[] = [];

  switch (step) {
    case 0: {
      if (!validateEmail(data.email)) {
        errors.push('Please enter a valid email address');
      }
      const passwordValidation = validatePassword(data.password);
      if (!passwordValidation.isValid) {
        errors.push(passwordValidation.errors[0]);
      }
      if (data.fullName.trim().length < 2) {
        errors.push('Please enter your full name (at least 2 characters)');
      }
      if (!validatePhoneNumber(data.phoneNumber)) {
        errors.push('Please enter a valid phone number');
      }
      break;
    }

    case 1:
      if (!data.selectedPersonality) {
        errors.push('Please select a coach personality');
      }
      break;

    case 3:
      if (!isValidTime(data.morningTime)) {
        errors.push('Please enter a valid morning time');
      }
      if (!isValidTime(data.eveningTime)) {
        errors.push('Please enter a valid evening time');
      }
      if (data.morningTime === data.eveningTime) {
        errors.push('Morning and evening times must be different');
      }
      break;

    case 4:
      if (data.intensityLevel < 1 || data.intensityLevel > 5) {
        errors.push('Please select an intensity level between 1 and 5');
      }
      break;

    case 5:
      if (data.additionalPreferences.length > 500) {
        errors.push('Additional preferences must be 500 characters or less');
      }
      break;
  }

  return errors;
}
